import React, { Component } from "react"
import Relay from "react-relay"
import { FormattedMessage } from "react-intl"
import MapBox from "../elements/MapBox"
import { getMapBox } from "../../scripts/mapBox"
import { geoNameAPI } from "../../scripts/geonames"
import resourceMessages from "../../messages/resource"

const TYPE_CAMERA = "Feature"
const TYPE_MAP_AREA = "polygon"
const TYPE_POINT = "Point"

class ResourceLocation extends Component {
  state = { linkHref: null, linkText: null }

  static defaultProps = {
    zoom: 15
  }

  onMapLoaded = map => {
    const mapboxgl = getMapBox()
    const {
      resource: { location_data }
    } = this.props

    if (location_data) {
      const locationData = JSON.parse(location_data)
      const type = locationData.type
      switch (type) {
        case TYPE_POINT:
          this.drawPoint(map, locationData, "/images/map/crosshair.png")
          this.displayLocation()
          break
        case TYPE_CAMERA:
          this.drawCamera(map, locationData)
          this.displayLocation()
          break
        case TYPE_MAP_AREA:
          this.drawMapArea(map, locationData)
          geoNameAPI("getJSON", { geonameId: locationData.geonameId }).then(
            res => {
              this.setState({
                linkHref: `http://www.geonames.org/${locationData.geonameId}`,
                linkText: `Location: ${res.name}`
              })
            }
          )
          break
        default:
      }
      this.setState({ type })
    }

    map.addControl(new mapboxgl.FullscreenControl(), "top-right")
    map.addControl(new mapboxgl.NavigationControl(), "bottom-right")
  }

  fullScreenHack = () => {
    // TODO - tim can this be tidied, and needs to be removed on component unmount?
    // annoying bug in safari where going to fullscreen wouldn't trigger a map redraw, so we ended up in fullscreen but with the map still being physical size as pre-fullscreen. So, force map redraw
    if ("onfullscreenchange" in window.document) {
      this.fullScreenEvent = "fullscreenchange"
    } else if ("onmozfullscreenchange" in window.document) {
      this.fullScreenEvent = "mozfullscreenchange"
    } else if ("onwebkitfullscreenchange" in window.document) {
      this.fullScreenEvent = "webkitfullscreenchange"
    } else if ("onmsfullscreenchange" in window.document) {
      this.fullScreenEvent = "MSFullscreenChange"
    }

    window.addEventListener(this.fullScreenEvent, this.onFullscreenChange)
  }

  onFullscreenChange = () => {
    setTimeout(() => {
      map.resize()
    }, 20)
  }

  componentWillUnmount() {
    if (this.fullScreenEvent) {
      window.removeEventListener(this.fullScreenEvent, this.onFullscreenChange)
    }
  }

  displayLocation = () => {
    const {
      resource: { location }
    } = this.props
    this.setState({
      linkText: `${parseFloat(location[1]).toFixed(6)}, ${parseFloat(
        location[0]
      ).toFixed(6)}`
    })
  }

  drawPoint = (
    map,
    geoJSON,
    imageSrc,
    layout = {},
    imageWidth = 100,
    imageHeight = 100
  ) => {
    this.addImage(imageSrc, imageWidth, imageHeight).then(img => {
      // map.loadImage(imageSrc, function(error, img) { // alternate image import method which seems to work better sometimes with svg?
      map.addImage("pointIcon", img)
      const layer = {
        id: "locationPoint",
        type: "symbol",
        source: {
          type: "geojson",
          data: geoJSON
        },
        layout: {
          "icon-image": "pointIcon",
          "icon-size": 0.9,
          ...layout
        }
      }
      map.addLayer(layer)
    })
  }

  drawMapArea = (map, locationData) => {
    this.drawPolygon(map, locationData.coordinates[0])
  }

  drawPolygon = (map, pointList, layerOptions, fit = 30) => {
    const mapboxgl = getMapBox()
    const layer = {
      id: "polygon",
      type: "fill",
      paint: {
        "fill-color": "#7DD3DE",
        "fill-opacity": 0.6
      },
      ...layerOptions,
      source: {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [pointList]
          }
        }
      }
    }

    map.addLayer(layer)

    if (fit) {
      const bounds = pointList.reduce(
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(pointList[0], pointList[0])
      )

      map.fitBounds(bounds, {
        padding: fit
      })
    }
  }

  drawCamera = (map, locationData) => {
    const geoJSON = locationData.geometry
    const pointList = [
      geoJSON.geometries[1].coordinates[0],
      geoJSON.geometries[0].coordinates,
      geoJSON.geometries[1].coordinates[1]
    ]

    const cameraGeoJSON = {
      coordinates: geoJSON.geometries[0].coordinates,
      type: "Point"
    }

    const { bearing } = locationData.properties
    this.drawPoint(
      map,
      cameraGeoJSON,
      "/images/map/camera.png",
      {
        "icon-rotate": parseFloat(bearing),
        "icon-size": 0.8,
        "icon-rotation-alignment": "map"
      },
      59,
      37
    )

    this.drawPolygon(map, pointList, { id: "cameraFill" }, 110)
  }

  addImage = (url, width, height) => {
    return new Promise(resolve => {
      const img = new Image(width, height)
      img.onload = () => resolve(img)
      img.src = url
    })
  }

  render() {
    const {
      resource: { id, location_data, location },
      height,
      zoom
    } = this.props
    const { linkHref, linkText } = this.state
    const options = { zoom }
    const hasCenter = Array.isArray(location) && location.length === 2
    if (hasCenter) {
      options.center = location
    }

    return !!location_data ? (
      <div className={this.props.className}>
        <div className="c-article__location-map">
          <MapBox id={id} onMapLoaded={this.onMapLoaded} mapOptions={options} />
        </div>
        {linkText && (
          <dl className="c-article__defs">
            <div className="list__def list__def--inline links--dark ">
              <dt>
                <FormattedMessage {...resourceMessages.location} />
              </dt>
              <dd>
                {linkHref && (
                  <a href={linkHref} target="_blank">
                    {linkText}
                  </a>
                )}
                {!linkHref && <span>{linkText}</span>}
              </dd>
            </div>
          </dl>
        )}
      </div>
    ) : null
  }
}

export default Relay.createContainer(ResourceLocation, {
  fragments: {
    resource: () => Relay.QL`
      fragment on Resource {
        id
        location_data
        location
      }
    `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/ResourceLocation.js